/**
 * SR Cart Offers — "Goes Well With" (2026-09-17)
 *
 * Replaces a paid cart-upsell app. Adds the chosen variant through the same
 * /cart/add.js + sections round-trip the theme's own product form uses, then
 * dispatches the theme's CartAddEvent so the cart drawer, the cart bubble and
 * the free-shipping bar all refresh exactly as they do everywhere else.
 */
import { CartAddEvent } from '@theme/events';

const RESET_DELAY = 2500;

class SrCartOffers extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    clearTimeout(this.#timer);
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const opener = target.closest('[data-sr-offer-open]');
    if (opener) {
      this.#toggleSizes(opener);
      return;
    }

    const variantButton = target.closest('[data-sr-offer-variant]');
    if (variantButton instanceof HTMLButtonElement) this.#add(variantButton);
  };

  /** @param {Element} opener */
  #toggleSizes(opener) {
    const card = opener.closest('[data-sr-offer]');
    const sizes = card?.querySelector('[data-sr-offer-sizes]');
    if (!(sizes instanceof HTMLElement)) return;

    // Only one size row open at a time.
    for (const other of this.querySelectorAll('[data-sr-offer-sizes]')) {
      if (other !== sizes && other instanceof HTMLElement) {
        other.hidden = true;
        other.closest('[data-sr-offer]')?.querySelector('[data-sr-offer-open]')?.setAttribute('aria-expanded', 'false');
      }
    }

    const nowOpen = sizes.hidden;
    sizes.hidden = !nowOpen;
    opener.setAttribute('aria-expanded', String(nowOpen));
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    if (button.disabled) return;
    const variantId = Number(button.dataset.srOfferVariant);
    if (!variantId) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });
      const payload = await response.json();
      if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'Could not add');

      this.#announce('Added to your cart.');
      this.#tagOfferAdd(variantId);
      this.dispatchEvent(
        new CartAddEvent({}, 'sr-cart-offers', {
          source: 'sr-cart-offers',
          itemCount: 1,
          variantId: String(variantId),
          productId: button.dataset.srOfferProduct,
          sections: payload.sections,
        })
      );
    } catch (error) {
      this.#announce(error instanceof Error ? error.message : 'Could not add that one.');
      button.disabled = false;
      button.textContent = original;
      return;
    }

    button.textContent = 'Added';
    this.#timer = setTimeout(() => {
      button.disabled = false;
      button.textContent = original;
    }, RESET_DELAY);
  }

  /**
   * Record the variant on the cart as a private attribute, `_sr_offer_adds`, so the
   * order shows which shirts came from this row (2026-09-23). Underscore attributes
   * are hidden from the shopper at checkout. Fire and forget: tracking must never
   * get in the way of the add itself.
   * @param {number} variantId
   */
  async #tagOfferAdd(variantId) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const ids = new Set(String(cart?.attributes?._sr_offer_adds || '').split(',').filter(Boolean));
      ids.add(String(variantId));
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _sr_offer_adds: [...ids].join(',') } }),
      });
    } catch {
      // Tracking only.
    }
  }

  /** @param {string} message */
  #announce(message) {
    const status = this.querySelector('[data-sr-offer-status]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('sr-cart-offers')) {
  customElements.define('sr-cart-offers', SrCartOffers);
}
